<script lang="ts" setup>
import type { DSTopic } from '@/types'

const props = defineProps({
  theme: {
    type: String,
    default: 'light',
  },
})

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { loadNextTopics, hasMoreTopics } = useTopicsSortedByUpdated()
const { user, isUserLoggedIn, avatarImageUrl } = storeToRefs(useUserStore())
const { getUnreadRecords } = useNotificationBase()
const { $tracker } = useNuxtApp()

const unreadCount = ref(0)
const loaderGuard = ref<HTMLDivElement | null>(null)
const isMenuVisible = ref(false)

const topicList = ref<DSTopic[]>([])
const tempTopicList = ref<DSTopic[]>([])

watch(tempTopicList, (newList) => {
  topicList.value.push(...newList)
}, {
  immediate: true,
})

let observer: IntersectionObserver | null = null

function initObserver() {
  if (observer !== null)
    observer.disconnect()

  observer = new IntersectionObserver(async (entries) => {
    const entry = entries[0]
    if (entry.isIntersecting) {
      if (hasMoreTopics.value) {
        tempTopicList.value = await loadNextTopics(topicList.value)
      } else {
        observer?.disconnect()
      }
    }
  })

  // await nextTick()
  if (loaderGuard.value)
    observer.observe(loaderGuard.value)
}

watch(isMenuVisible, async (value) => {
  if (value) {
    if (topicList.value.length === 0) {
      tempTopicList.value = await loadNextTopics([])
    }
    initObserver()
    $tracker('show_navbar_menu')
  } else {
    observer?.disconnect()
    $tracker('hide_navbar_menu')
  }
})

watch(user, async (value) => {
  if (!value)
    return
  unreadCount.value = await getUnreadRecords()
})

const showEnglishLabels = computed(() => locale.value === 'tw')

const themeClass: { [key: string]: { style: string, color: string, logo: string } } = {
  dark: {
    style: 'bg-neutral-900',
    color: 'white',
    logo: '/images/hero-logo.png',
  },
  light: {
    style: 'bg-silver',
    color: 'black',
    logo: '/images/hero-logo-black.png',
  },
  gradient: {
    style: 'bg-gradient-dark-reverse w-full absolute z-40',
    color: 'white',
    logo: '/images/hero-logo.png',
  },
  transparent: {
    style: 'bg-transparent absolute right-0 z-40',
    color: 'white',
    logo: '',
  },
}
const currentTheme = computed(() => themeClass[props.theme])
</script>

<template>
  <nav class="nav">
    <div class="flex items-center justify-between px-4 py-[14px]" :class="currentTheme.style">
      <nuxt-link :to="localePath('/')" class="w-[25%] max-w-[100px]">
        <img v-if="theme !== 'transparent'" :src="currentTheme.logo" title="Dear Stranger">
      </nuxt-link>
      <div class="flex items-center gap-3">
        <!-- 通知頁入口 -->
        <client-only>
          <nuxt-link
            v-if="isUserLoggedIn" :to="localePath('/notifications')"
            class="relative at-center"
            track-component="nav-notification"
          >
            <i i-ph-bell-light :style="`font-size: 24px; color: ${currentTheme.color};`" />
            <i
              v-if="unreadCount > 0" i-ph-circle-fill style="font-size: 10px; color: red;"
              class="absolute right-0 top-0"
            />
          </nuxt-link>
          <!-- 登入頁入口 -->
          <nuxt-link
            v-if="!isUserLoggedIn" :to="localePath('/login')" class="at-center" no-prefetch
            track-component="nav-avatar"
          >
            <i i-ph-user-light :style="`font-size: 24px; color: ${currentTheme.color};`" />
          </nuxt-link>
          <!-- 會員頁入口 -->
          <nuxt-link v-else :to="localePath('/profile')" class="at-center" track-component="nav-avatar">
            <div class="rounded-full bg-white w-[24px] h-[24px]">
              <img
                v-show="isUserLoggedIn"
                class="w-full rounded-full h-full object-contain"
                :src="avatarImageUrl ?? ''"
                :alt="user?.name"
              >
            </div>
          </nuxt-link>
        </client-only>
        <ui-sheet v-model:open="isMenuVisible">
          <ui-sheet-trigger class="at-center">
            <i i-ph-equals-light :style="`font-size: 28px; color: ${currentTheme.color};`" />
          </ui-sheet-trigger>
          <ui-sheet-content
            class="w-full pt-safe bg-neutral-900 border-l-0 data-[state=open]:duration-300"
          >
            <ui-sheet-header hidden>
              <ui-sheet-title>Navigation</ui-sheet-title>
              <ui-sheet-description />
            </ui-sheet-header>
            <div class="flex flex-col pt-6">
              <nuxt-link
                class="text-white nav-link opacity-50" :to="localePath('')"
              >
                {{ t('common.navbar.side-menu.themes') }}<small
                  v-if="showEnglishLabels"
                  class="text-base pl-4"
                >Themes</small>
              </nuxt-link>
              <nuxt-link class="nav-link text-white" :to="localePath('/?cate=0')">
                {{ t('common.navbar.side-menu.explore-stories') }}<small
                  v-if="showEnglishLabels"
                  class="pl-4 text-base"
                >Stories</small>

                <ui-skeleton v-if="!topicList.length" class="h-[200px] mt-4" />
                <div
                  v-else
                  class="flex text-xl pt-4 overflow-x-auto min-h-min flex-nowrap"
                >
                  <common-nav-topic-card
                    v-for="topic in topicList"
                    :key="topic.id"
                    class="grow-0 shrink-0"
                    :topic="topic"
                    @click="isMenuVisible = false"
                  />
                </div>
                <div ref="loaderGuard" class="w-[1px]" />
              </nuxt-link>
              <nuxt-link class="nav-link text-white" :to="localePath('/posts')">
                {{ t('common.navbar.side-menu.create-story') }}<small
                  v-if="showEnglishLabels"
                  class="pl-4 text-base"
                >Create</small>
              </nuxt-link>
              <nuxt-link class="nav-link text-white" :to="localePath('/?cate=1')" @click="isMenuVisible = false">
                {{ t('common.navbar.side-menu.browse-diaries') }}<small
                  v-if="showEnglishLabels"
                  class="pl-4 text-base"
                >Diary</small>
              </nuxt-link>
              <nuxt-link class="nav-link text-white" :to="localePath('/diaries')">
                {{ t('common.navbar.side-menu.my-diary') }}<small
                  v-if="showEnglishLabels"
                  class="pl-4 text-base"
                >My Diary</small>
              </nuxt-link>
              <nuxt-link class="nav-link text-white" :to="localePath('/about')">
                {{ t('common.navbar.side-menu.about') }}<small
                  v-if="showEnglishLabels"
                  class="pl-4 text-base"
                >About</small>
              </nuxt-link>
              <!-- <button
                class="w-full nav-link"
                type="primary"
                size="large"
                @click.prevent="logout"
              >
                登出<small
                  class="pl-4 text-base"
                >Sign Out</small>
              </button> -->
            </div>
          </ui-sheet-content>
        </ui-sheet>
      </div>
    </div>
  </nav>
</template>

<style>
:root {
  --scroll-bar-color: #c5c5c5;
  --scroll-bar-bg-color: transparent;
}

::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.5);
}

/* Works on Chrome, Edge, and Safari */
*::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

*::-webkit-scrollbar-track {
  background: var(--scroll-bar-bg-color);
}

*::-webkit-scrollbar-thumb {
  background-color: var(--scroll-bar-color);
  border-radius: 20px;
  border: 3px solid var(--scroll-bar-bg-color);
}

.nav-link {
  @apply block h-auto px-0 py-3;
  @apply text-left text-3xl;
  @apply border-gray-700 border-b-[1px] border-gray-700;
}
</style>
